import { IconButton, Menu, MenuItem } from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import { useContext, useState } from "react";
import TranscriptRequestDialog from "./TranscriptRequestDialog";
import { useTranslation } from "react-i18next";
import { useTranscriptRequest } from "../../hooks/useTranscription";
import { useAlert } from "../../context/AlertContext";
import { AuthContext } from "../../context/AuthContext";

interface TranscriptionOptionsMenuProps {
  customerId: string;
  interactionId: string;
  hasExistingTranscript?: boolean;
  onTranscriptionStarted?: (jobId: string) => void;
}

export function TranscriptionOptionsMenu({
  customerId,
  interactionId,
  hasExistingTranscript = false,
  onTranscriptionStarted,
}: Readonly<TranscriptionOptionsMenuProps>) {
  const { mutateAsync: transcribeAsync } = useTranscriptRequest();
  const showAlert = useAlert();
  const { user } = useContext(AuthContext);
  const { t } = useTranslation(["transcription"]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRequestTranscript = async () => {
    if (!userHasPermissionToRedact()) {
      handleClose();
      const result = await transcribeAsync({
        customerId,
        interactionId,
      });
      onTranscriptionStarted?.(result.jobId);
      showAlert(t("transcription:transcriptRequestSubmitted"), "success");
      return;
    }
    setIsDialogOpen(true);
    handleClose();
  };

  function userHasPermissionToRedact(): boolean {
    return user?.hasPermission("interactions:Redact") ?? false;
  }

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <MoreVert />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleRequestTranscript}>
          {t("transcription:requestTranscript")}
        </MenuItem>
      </Menu>
      <TranscriptRequestDialog
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        customerId={customerId}
        interactionId={interactionId}
        hasExistingTranscript={hasExistingTranscript}
        onTranscriptionStarted={onTranscriptionStarted}
      />
    </>
  );
}
